import { AxiosRequestConfig } from "axios";
import { api, prefix, generateQueryParamFromObj } from "@/api";
import {
  AddOpportunityRoleParamsType,
  OpportunitiesParamsType,
  OpportunityHistoryItem,
  OpportunityRelatedEntity,
  OpportunityRoleLightType,
  OpportunityRoleProfileType,
  OpportunityRoleType,
  OpportunityType,
  OpportunityWithEntityParamsType,
  ScopePhaseType,
  ScopeRelatedEntity,
  ScopeType,
} from "@/types/scopes";
import { MacroStatusType } from "@/types/macroStatus";
import { LeadActionType } from "@/types/lead";
import { SystemRoles } from "@/types/systemRoles";
import { AssigneeInfoType, AssignmentRuleType, UserVisibilityType } from "@/types/assignee";
import { OpportunityNetworkRoleRuleType } from "@/types/opportunityNetworkRoleRules";

const namespace = "gaia/scopes";

export interface OpportunityEntity {
  id: number;
  entityType: ScopeRelatedEntity;
  entityId: string;
}

export const getScopes = (params: any = {}, config: AxiosRequestConfig = {}) => {
  const query = generateQueryParamFromObj(params);
  return api<{ data: { scopes: ScopeType[] }; count: number }>({
    url: `${prefix}/${namespace}?${query}`,
    method: "get",
    ...config,
  });
};

// GAIA-2536: destinations for "Cambio Ambito" are computed server-side against the opportunity's real
// entities (service-scope), so the list matches exactly what the change endpoint will accept.
export const getCompatibleScopesForOpportunity = (
  opportunityId: number,
  config: AxiosRequestConfig = {}
) => {
  return api<{ data: { scopes: ScopeType[] }; count: number }>({
    url: `${prefix}/${namespace}/compatible/${opportunityId}`,
    method: "get",
    ...config,
  });
};

export const getOpportunityNetworkRoleRules = (
  scopeIds: string,
  config: AxiosRequestConfig = {}
) => {
  return api<{
    data: { opportunityNetworkRoleRules: OpportunityNetworkRoleRuleType[] };
    count: number;
  }>({
    url: `${prefix}/${namespace}/opportunities/networkrolerules?scopeids=${scopeIds}`,
    method: "get",
    ...config,
  });
};

export const getOpportunities = (
  params: OpportunitiesParamsType = {},
  config: AxiosRequestConfig = {}
) => {
  const query = generateQueryParamFromObj(params);
  return api<{ data: { opportunities: OpportunityType[] }; count: number }>({
    url: `${prefix}/${namespace}/opportunities?${query}`,
    method: "get",
    ...config,
  });
};

export const getOpportunityAssignees = (filters?: any, config: AxiosRequestConfig = {}) => {
  const query = filters ? generateQueryParamFromObj(filters) : "";
  return api<{ assignees: AssigneeInfoType[] }>({
    url: `${prefix}/${namespace}/opportunities/assignees/available?${query.length ? query : ""}`,
    method: "get",
    ...config,
  });
};

export const getMacroStatuses = (params: any = {}, config: AxiosRequestConfig = {}) => {
  const query = generateQueryParamFromObj(params);
  return api<{ data: { macroStatuses: MacroStatusType[] }; count: number }>({
    url: `${prefix}/${namespace}/macrostatuses${query ? `?${query}` : ""}`,
    method: "get",
    ...config,
  });
};
export const getScopePhases = (config: AxiosRequestConfig = {}) => {
  return api<{ opportunitiesScopePhases: ScopePhaseType[] }>({
    url: `${prefix}/${namespace}/opportunities/opportunityscopephases`,
    method: "get",
    ...config,
  });
};

export const getOpportunity = (
  id: string, 
  params:{includeTransitionRules?: boolean} = {}, 
  config: AxiosRequestConfig = {}
) => {
  const query = generateQueryParamFromObj(params);
  return api<{ actions: LeadActionType[]; data: OpportunityType }>({
    url: `${prefix}/${namespace}/opportunities/${id}${query ? `?${query}` : ""}`,
    method: "get",
    ...config,
  });
};

export const createOpportunity = (params: object, config: AxiosRequestConfig = {}) => {
  return api<{ id: number }>({
    url: `${prefix}/${namespace}/opportunities`,
    method: "POST",
    data: params,
    ...config,
  });
};

type OpportunityWithEntityResponse =
  | { opportunityId: number; invitationId: number }
  | { opportunityId: number; organizationId: number };

export const manageOpportunityWithRelatedEntity = (
  params: OpportunityWithEntityParamsType,
  entityType: string,
  config: AxiosRequestConfig = {}
) => {
  return api<OpportunityWithEntityResponse>({
    url: `${prefix}/${namespace}/opportunities/${entityType}`,
    method: "POST",
    data: params,
    ...config,
  });
};

export const createOpportunityEntity = (
  opportunityId: number,
  params: { entityId: string; entityType: string; parentId?: number },
  config: AxiosRequestConfig = {}
) => {
  return api<{ id: number }>({
    url: `${prefix}/${namespace}/opportunities/${opportunityId}/entities`,
    method: "POST",
    data: params,
    ...config,
  });
};

export const updateOpportunity = (id: number, params: object, config: AxiosRequestConfig = {}) => {
  return api<{ id: number }>({
    url: `${prefix}/${namespace}/opportunities/${id}`,
    method: "PUT",
    data: params,
    ...config,
  });
};

export const deleteOpportunity = (id: number, config: AxiosRequestConfig = {}) => {
  return api<void>({
    url: `${prefix}/${namespace}/opportunities/${id}`,
    method: "DELETE",
    ...config,
  });
};

export const getOpportunityEntitiesById = (
  id: number,
  params: any,
  config: AxiosRequestConfig = {}
) => {
  const query = generateQueryParamFromObj(params);
  return api<{
    data: { opportunityEntityDetails: OpportunityRelatedEntity[] };
    count: number;
  }>({
    url: `${prefix}/${namespace}/opportunities/${id}/opportunityentities/details?${query}`,
    method: "get",
    ...config,
  });
};

export const getOpportunityAssigneeByOpportunityId = (
  opportunityId: string | number,
  config: AxiosRequestConfig = {}
) => {
  return api<{ assignees: AssigneeInfoType[] | null }>({
    url: `${prefix}/${namespace}/opportunities/${opportunityId}/assignees/actual`,
    method: "GET",
    ...config,
  });
};
export const getOpportunityAssigneeByOpportunityIdv2 = (
  opportunityId: string | number,
  config: AxiosRequestConfig = {}
) => {
  return api<{ assignees: AssigneeInfoType[] | null }>({
    url: `${prefix}/${namespace}/opportunities/${opportunityId}/roles/150/users/actual`,
    method: "GET",
    ...config,
  });
};

export const getUsersVisibilityOpportunity = (
  opportunityId: string | number,
  config: AxiosRequestConfig = {}
) => {
  return api<{ roles: UserVisibilityType[] }>({
    url: `${prefix}/${namespace}/opportunities/${opportunityId}/visibility`,
    method: "GET",
    ...config,
  });
}

export const getOpportunityAssigneesByScopeId = (
  scopeId: number,
  config: AxiosRequestConfig = {}
) => {
  const query = scopeId ? `?scopeIds=${scopeId}` : "";
  return api<{ assignees: AssigneeInfoType[] }>({
    url: `${prefix}/${namespace}/opportunities/assignees/available${query}`,
    method: "GET",
    ...config,
  });
};
export const getOpportunityAssigneesByScopeIdv2 = (
  // scopeId: number,
  params: {
    scopeIds?: number | string,
    opportunityIds?: number | string,
  },
  config: AxiosRequestConfig = {}
) => {
  // const query = scopeId ? `?scopeIds=${scopeId}` : "";
  const query = generateQueryParamFromObj(params);
  return api<{ assignees: AssigneeInfoType[] }>({
    url: `${prefix}/${namespace}/opportunities/roles/150/users/available?${query}`,
    method: "get",
    ...config,
  });
};

export const getOpportunityObserversByScopeId = (
  // scopeId: number,
  params: {
    scopeIds?: number | string,
    opportunityIds?: number,
  },
  config: AxiosRequestConfig = {}
) => {
  // const query = scopeId ? `?scopeIds=${scopeId}` : "";
  const query = generateQueryParamFromObj(params);
  return api<{ assignees: AssigneeInfoType[] }>({
    url: `${prefix}/${namespace}/opportunities/roles/151/users/available?${query}`,
    method: "get",
    ...config,
  });
};

export const getOpportunityObserversByOpportunityId = (
  opportunityId: string | number,
  config: AxiosRequestConfig = {}
) => {
  const query = `?roleIds=${SystemRoles.VIEWER}&opportunityIds=${opportunityId}`;
  return api<{ assignees: AssigneeInfoType[] }>({
    url: `${prefix}/${namespace}/opportunities/${opportunityId}/roles/151/users/actual`,
    method: "get",
    ...config,
  });
};

export const getOpportunityRolesByOpportunityId = (
  opportunityId: string | number,
  roleId: string | number,
  config: AxiosRequestConfig = {}
) => {
  const query = `?opportunityIds=${opportunityId}&roleIds=${roleId}`;
  return api<{ data: { opportunityRoles: OpportunityRoleType[] } }>({
    url: `${prefix}/${namespace}/opportunities/v2/opportunityroles${query}`,
    method: "get",
    ...config,
  });
};

export const createOpportunityRoles = (
  commands: AddOpportunityRoleParamsType[],
  config: AxiosRequestConfig = {}
) => {
  return api<{ id: number }>({
    url: `${prefix}/${namespace}/opportunities/opportunityroles`,
    method: "POST",
    data: { commands: commands },
    ...config,
  });
};

export const removeOpportunityRole = (roleId: number, config: AxiosRequestConfig = {}) => {
  return api<void>({
    url: `${prefix}/${namespace}/opportunities/opportunityrole/${roleId}`,
    method: "DELETE",
    ...config,
  });
};

export const assignOpportunity = (
  id: number | string,
  params: {
    organizationId: number;
    roleId: number;
    userId: string;
  },
  config: AxiosRequestConfig = {}
) => {
  return api<{ id: number }>({
    url: `${prefix}/${namespace}/opportunities/${id}/opportunityrole`,
    method: "POST",
    data: params,
    ...config,
  });
};

export const deleteAssignedOpportunity = (id: number | string, config: AxiosRequestConfig = {}) => {
  return api<void>({
    url: `${prefix}/${namespace}/opportunities/opportunityrole/${id}`,
    method: "DELETE",
    ...config,
  });
};

export const getOpportunityEntitiesByScopeId = (
  scopeId: number,
  config: AxiosRequestConfig = {},
  params?: any
) => {
  const query = generateQueryParamFromObj(params);
  return api<{ data: { opportunityEntities: OpportunityEntity[] }; count: number }>({
    url: `${prefix}/${namespace}/${scopeId}/opportunityentities?${query}`,
    method: "get",
    ...config,
  });
};

export const getOpportunityAssignmentRule = (config: AxiosRequestConfig = {}) => {
  return api<{
    data: {
      rules: AssignmentRuleType[];
    };
  }>({
    url: `${prefix}/${namespace}/opportunities/exclusivity/rules`,
    method: "get",
    ...config,
  });
};

export const getOpportunityHistory = (id: string | number, config: AxiosRequestConfig = {}) => {
  return api<{ data: { history: OpportunityHistoryItem[] } }>({
    url: `${prefix}/${namespace}/opportunities/${id}/history?includeScopePhaseCause=true&includeScopePhase=true`,
    method: "get",
    ...config,
  });
};

export const getOpportunityRoles = (config: AxiosRequestConfig = {}, roleIds?: number[]) => {
  return api<{
    data: {
      identifiers: OpportunityRoleLightType[];
    };
  }>({
    url: `${prefix}/${namespace}/opportunities/opportunityroles/identifiers${
      roleIds && roleIds.length ? `?roleIds=${roleIds.join(",")}` : ""
    }`,
    method: "get",
    ...config,
  });
};
